import { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Phone, MapPin, Calendar as CalIcon, MessageSquare, Mail,
  StickyNote, FileText, MoreHorizontal,
} from 'lucide-react';

/**
 * Diálogo reutilizable de registro de actividad.
 *
 * Props:
 *  - open / onOpenChange: control externo del diálogo.
 *  - leadId (opcional): vincula la actividad al lead.
 *  - opportunityId (opcional): vincula a oportunidad.
 *  - leadName (opcional): se muestra como contexto.
 *  - onSuccess (opcional): callback al guardar OK.
 *
 * Al menos uno de leadId / opportunityId debe estar presente.
 */
const ACTIVITY_OPTIONS = [
  { key: 'call',        label: 'Llamada',           Icon: Phone },
  { key: 'visit',       label: 'Visita presencial', Icon: MapPin },
  { key: 'appointment', label: 'Cita / Reunión',    Icon: CalIcon },
  { key: 'message',     label: 'Mensaje WhatsApp',  Icon: MessageSquare },
  { key: 'email',       label: 'Correo',            Icon: Mail },
  { key: 'note',        label: 'Nota seguimiento',  Icon: StickyNote },
  { key: 'brochure',    label: 'Envío ficha',       Icon: FileText },
  { key: 'other',       label: 'Otra',              Icon: MoreHorizontal },
];

const APPOINTMENT_TYPES = new Set(['appointment', 'visit']);
// Accionables que exigen SOLO fecha (sin hora) — "note"/"brochure" quedan
// sin fecha obligatoria, igual que hoy.
const DATE_REQUIRED_TYPES = new Set(['call', 'message', 'email', 'other']);

export default function RegisterActivityDialog({ open, onOpenChange, ...rest }) {
  // Cada apertura desmonta y remonta el form para que el estado interno
  // arranque limpio sin necesidad de setState dentro de useEffect.
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <RegisterActivityDialogInner
          onOpenChange={onOpenChange}
          {...rest}
        />
      )}
    </Dialog>
  );
}

function RegisterActivityDialogInner({
  onOpenChange,
  leadId = null,
  opportunityId = null,
  leadName = '',
  onSuccess,
}) {
  const [type, setType] = useState('note');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [location, setLocation] = useState('');
  const [saving, setSaving] = useState(false);
  // A4: default "La estoy programando" — no cambia el comportamiento actual.
  const [alreadyHappened, setAlreadyHappened] = useState(false);

  const isAppt = APPOINTMENT_TYPES.has(type);
  const needsDateOnly = DATE_REQUIRED_TYPES.has(type);
  const isCall = type === 'call';
  const todayStr = new Date().toISOString().slice(0, 10);

  const handleSave = async () => {
    if (isAppt && (!date || !time)) {
      toast.error('Fecha y hora son obligatorias para citas/visitas.');
      return;
    }
    if (needsDateOnly && !date) {
      toast.error('Fecha es obligatoria para este tipo de actividad.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        type,
        description: description.trim(),
        lead_id: leadId || null,
        opportunity_id: opportunityId || null,
      };
      if (isAppt) {
        body.appointment_date = date;
        body.appointment_time = time;
        body.location = location.trim();
      }
      if (needsDateOnly) {
        body.appointment_date = date;
      }
      if (isCall) {
        // A4: el backend SOLO respeta este campo para type=call — cualquier
        // otro tipo lo ignora aunque venga en true.
        body.already_happened = alreadyHappened;
      }
      const res = await api.post('/activities', body);
      const data = res.data || {};
      if (isAppt) {
        toast.success(data?.notified_vendor
          ? 'Cita registrada y notificada al vendor'
          : 'Cita registrada');
      } else {
        toast.success('Actividad registrada');
      }
      onSuccess?.(data?.activity);
      onOpenChange?.(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Error al registrar actividad';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="max-w-lg bg-card border-white/10" data-testid="register-activity-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Registrar actividad</DialogTitle>
          <DialogDescription className="text-muted-foreground text-xs">
            {leadName ? `Contexto: ${leadName}` : 'Selecciona el tipo de actividad'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Selector tipo (grid 4 cols con iconos) */}
          <div>
            <Label className="text-xs text-muted-foreground mb-2 block">Tipo</Label>
            <div className="grid grid-cols-4 gap-2" data-testid="activity-type-selector">
              {ACTIVITY_OPTIONS.map(({ key, label, Icon }) => {
                const active = type === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setType(key)}
                    className={`flex flex-col items-center justify-center gap-1 p-2 rounded-lg border text-[10px] transition-colors ${
                      active
                        ? 'bg-primary/15 border-primary/40 text-primary'
                        : 'bg-black/20 border-white/5 text-muted-foreground hover:border-white/15'
                    }`}
                    data-testid={`activity-type-${key}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Cita: fecha, hora, ubicación */}
          {isAppt && (
            <div className="space-y-2" data-testid="activity-appt-fields">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-muted-foreground">Fecha</Label>
                  <Input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="bg-black/20 border-white/10 mt-1"
                    data-testid="activity-appt-date"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Hora</Label>
                  <Input
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    className="bg-black/20 border-white/10 mt-1"
                    data-testid="activity-appt-time"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Ubicación</Label>
                <Input
                  placeholder="Showroom, dirección, link de videollamada…"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="bg-black/20 border-white/10 mt-1"
                  data-testid="activity-appt-location"
                />
              </div>
            </div>
          )}

          {/* A4: Llamada realizada vs. programada — solo afecta type=call */}
          {isCall && (
            <div className="grid grid-cols-2 gap-2" data-testid="activity-call-happened-toggle">
              <button
                type="button"
                onClick={() => setAlreadyHappened(false)}
                className={`p-2 rounded-lg border text-[11px] transition-colors ${
                  !alreadyHappened
                    ? 'bg-primary/15 border-primary/40 text-primary'
                    : 'bg-black/20 border-white/5 text-muted-foreground hover:border-white/15'
                }`}
                data-testid="activity-call-scheduling"
              >
                La estoy programando
              </button>
              <button
                type="button"
                onClick={() => {
                  setAlreadyHappened(true);
                  if (!date) setDate(todayStr);
                }}
                className={`p-2 rounded-lg border text-[11px] transition-colors ${
                  alreadyHappened
                    ? 'bg-primary/15 border-primary/40 text-primary'
                    : 'bg-black/20 border-white/5 text-muted-foreground hover:border-white/15'
                }`}
                data-testid="activity-call-happened"
              >
                Ya se realizó
              </button>
            </div>
          )}

          {/* Fecha (sin hora) para accionables que no son cita/visita.
              Punto 1: min/max solo para "call" (el único con el toggle de
              A4) -- "La estoy programando" exige hoy en adelante, "Ya se
              realizó" exige hoy hacia atrás. message/email/other quedan sin
              restricción, igual que hoy (no tienen el toggle). */}
          {needsDateOnly && (
            <div data-testid="activity-date-only-field">
              <Label className="text-xs text-muted-foreground">Fecha</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="bg-black/20 border-white/10 mt-1"
                data-testid="activity-date-only"
                min={isCall && !alreadyHappened ? todayStr : undefined}
                max={isCall && alreadyHappened ? todayStr : undefined}
              />
            </div>
          )}

          {/* Descripción / notas */}
          <div>
            <Label className="text-xs text-muted-foreground">Descripción / notas</Label>
            <Textarea
              placeholder="¿Qué se conversó, acordó o se enviará?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="bg-black/20 border-white/10 mt-1 text-sm"
              data-testid="activity-description"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => onOpenChange?.(false)}
              disabled={saving}
              data-testid="activity-cancel-btn"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-primary hover:bg-primary/90"
              data-testid="activity-save-btn"
            >
              {saving ? 'Guardando…' : 'Guardar actividad'}
            </Button>
          </div>
        </div>
      </DialogContent>
  );
}
